import { useCallback, useRef, useState } from 'react'

import { type AuthError, normalizeAuthError } from './errors'

type UseAuthMutationOptions<TData, TVariables> = {
  mutationFn: (variables: TVariables) => Promise<TData>
}

type MutateOptions<TData> = {
  onSuccess?: (data: TData) => void
  onError?: (error: AuthError) => void
}

export type UseAuthMutationResult<TData, TVariables> = {
  isPending: boolean
  isError: boolean
  error: AuthError | null
  reset: () => void
  mutate: (variables: TVariables, options?: MutateOptions<TData>) => void
}

export const useAuthMutation = <TData, TVariables>({
  mutationFn,
}: UseAuthMutationOptions<TData, TVariables>): UseAuthMutationResult<
  TData,
  TVariables
> => {
  const [isPending, setIsPending] = useState<boolean>(false)
  const [error, setError] = useState<AuthError | null>(null)

  // Keep latest mutationFn in a ref so mutate doesn't need it as a dep
  const mutationFnRef = useRef(mutationFn)
  mutationFnRef.current = mutationFn

  // Ref-based guard prevents double-invocation without destabilising mutate
  const isPendingRef = useRef(false)

  const isError = error !== null

  const reset = useCallback((): void => {
    setError(null)
    setIsPending(false)
    isPendingRef.current = false
  }, [])

  const mutate = useCallback(
    async (
      variables: TVariables,
      { onSuccess, onError }: MutateOptions<TData> = {}
    ): Promise<void> => {
      if (isPendingRef.current) return

      isPendingRef.current = true
      setError(null)
      setIsPending(true)

      try {
        const data = await mutationFnRef.current(variables)
        onSuccess?.(data)
      } catch (error) {
        const authError = normalizeAuthError(error)
        setError(authError)
        onError?.(authError)
      } finally {
        isPendingRef.current = false
        setIsPending(false)
      }
    },
    []
  )

  return { isPending, isError, error, reset, mutate }
}
